import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";
import { morph } from "@theme/morph";
import { formatCurrency, getLenis } from "@theme/utilities";

/**
 * Identity of a tile group: the option value ids it covers, in order. A change means the
 * product itself changed (combined listing) and the copy has to be rebuilt rather than synced.
 *
 * @param {Element} tiles
 * @returns {string}
 */
function tileSignature(tiles) {
  return Array.from(tiles.querySelectorAll("[data-option-value-id]"))
    .map((el) => /** @type {HTMLElement} */ (el).dataset.optionValueId)
    .join(",");
}

class StickyAddToCart extends Component {
  requiredRefs = ["stickyBar", "addToCartButton", "addToCartSpinner", "addToCartText"];

  #abortController = new AbortController();
  #buyButtonsIntersectionObserver = null;
  #footerSectionObserver = null;
  #isSticky = false;
  #hiddenInFooter = false;

  /**
   * The copy of the main picker's tile group living in the bar, or null when the product
   * template's variant picker is not set to the Tiles style. Owned by this component — the
   * server response never contains it, so it is detached around every morph.
   * @type {HTMLElement | null}
   */
  #tiles = null;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();
    const { signal } = this.#abortController;
    const target = this.closest(".shopify-section");

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });

    this.#mountTiles();
  }

  disconnectedCallback() {
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#footerSectionObserver?.disconnect();
    this.#abortController.abort();
    super.disconnectedCallback();
  }

  updatedCallback() {
    super.updatedCallback?.();
    // Re-setup observers with fresh DOM references after morph
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#footerSectionObserver?.disconnect();
    this.#setupIntersectionObserver();
    this.#mountTiles();
  }

  /**
   * The main picker's tile group, or null when the variant picker is not on the Tiles style.
   * Its presence is the whole feature switch: the tiles are a `_variant-picker` block setting,
   * which the section cannot read, so the rendered markup is what tells us the style is on.
   *
   * Product cards can carry their own pickers inside this section (bundles, match-and-save), so
   * the lookup is pinned to the main picker for this product.
   *
   * @returns {HTMLElement | null}
   */
  #getSourceTiles() {
    const { productId } = this.dataset;
    const section = this.closest(".shopify-section");
    if (!productId || !section) return null;

    const pickers = section.querySelectorAll(
      `variant-picker[ref="mainVariantPicker"][data-product-id="${productId}"]`
    );

    for (const picker of pickers) {
      if (picker.closest("product-card, quick-add-dialog")) continue;
      const tiles = picker.querySelector(".variant-option--tiles");
      if (tiles instanceof HTMLElement) return tiles;
    }

    return null;
  }

  /**
   * Mirror the main picker's tile group into the bar so the sticky UI offers the same choice as
   * the hero, styled by the same block settings. Cheap and idempotent — called on connect, after
   * every section morph, and after the bar re-renders on a variant change.
   */
  #mountTiles() {
    const { stickyBar } = this.refs;
    if (!stickyBar) return;

    const source = this.#getSourceTiles();

    if (!source) {
      this.#tiles?.remove();
      this.#tiles = null;
      stickyBar.classList.remove("sticky-add-to-cart__wrapper--tiles");
      return;
    }

    const signature = tileSignature(source);
    if (this.#tiles && this.#tiles.dataset.tileSignature !== signature) {
      this.#tiles.remove();
      this.#tiles = null;
    }

    const isNew = !this.#tiles;
    if (!this.#tiles) this.#tiles = this.#buildTiles(source, signature);

    if (this.#tiles.parentElement !== stickyBar) stickyBar.prepend(this.#tiles);
    stickyBar.classList.add("sticky-add-to-cart__wrapper--tiles");

    this.#syncTiles();
    if (isNew) this.#hydrateTileImages();
  }

  /**
   * @param {HTMLElement} source
   * @param {string} signature
   * @returns {HTMLElement}
   */
  #buildTiles(source, signature) {
    const tiles = /** @type {HTMLElement} */ (source.cloneNode(true));
    tiles.classList.add("sticky-add-to-cart__tiles");
    tiles.dataset.tileSignature = signature;

    // Theme editor attributes are the block's identity. Leaving them on a copy makes the editor
    // treat two elements as the same block and highlight/scroll to the wrong one.
    for (const el of tiles.querySelectorAll("[data-shopify-editor-block], [shopify-editor-block]")) {
      el.removeAttribute("data-shopify-editor-block");
      el.removeAttribute("shopify-editor-block");
    }

    // The image scheduler's state belongs to the picker's own images, not to copies of them.
    for (const img of tiles.querySelectorAll("img[data-img]")) img.removeAttribute("data-img");

    // The copies need their own radio group. Sharing the picker's `name` would put both sets in
    // one group for any tile that ends up outside a <form>, and they would deselect each other.
    // The copy never owns selection anyway — `#handleTileChange` forwards to the real input.
    for (const input of tiles.querySelectorAll('input[type="radio"]')) {
      /** @type {HTMLInputElement} */ (input).name = `${/** @type {HTMLInputElement} */ (input).name}-sticky`;
    }

    tiles.addEventListener("change", this.#handleTileChange);

    return tiles;
  }

  /**
   * The bar starts translated off-screen, so a copied tile image may sit unhydrated until the
   * bar first slides in and its lazy-load observer fires — long enough to show empty tiles.
   * The sources are the same URLs the main picker is already loading, so resolve them up front.
   * Deferred a frame so `responsive-image` has computed `sizes` from the copy's own width first.
   */
  #hydrateTileImages() {
    const tiles = this.#tiles;
    if (!tiles) return;

    requestAnimationFrame(() => {
      if (!tiles.isConnected) return;

      for (const el of tiles.querySelectorAll("source[data-srcset]:not([srcset]), img[data-srcset]:not([srcset])")) {
        el.setAttribute("srcset", /** @type {HTMLElement} */ (el).dataset.srcset ?? "");
      }
      for (const img of tiles.querySelectorAll("img[data-src]:not([src])")) {
        img.setAttribute("src", /** @type {HTMLElement} */ (img).dataset.src ?? "");
      }
    });
  }

  /**
   * Copy the live state of the main picker onto the tiles in the bar. Everything that can change
   * between variants — which tile is checked, which are sold out, the label's selected value — is
   * read straight off the picker, so the copy can never drift from it.
   */
  #syncTiles() {
    const tiles = this.#tiles;
    const source = this.#getSourceTiles();
    if (!tiles || !source) return;

    const sourceValue = source.querySelector(".variant-option__label-value");
    const tilesValue = tiles.querySelector(".variant-option__label-value");
    if (sourceValue && tilesValue) tilesValue.textContent = sourceValue.textContent;

    for (const el of tiles.querySelectorAll('input[type="radio"]')) {
      const input = /** @type {HTMLInputElement} */ (el);
      const match = source.querySelector(`input[data-option-value-id="${input.dataset.optionValueId}"]`);
      if (!(match instanceof HTMLInputElement)) continue;

      input.checked = match.checked;
      input.disabled = match.disabled;
      input.dataset.optionAvailable = match.dataset.optionAvailable ?? "true";
    }
  }

  /**
   * @param {Event} event
   */
  #handleTileChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    const source = this.#getSourceTiles();
    const target = source?.querySelector(`input[data-option-value-id="${input.dataset.optionValueId}"]`);
    if (!(target instanceof HTMLInputElement) || target.checked) return;

    // Click the real input rather than assigning `checked`: `variant-picker` drives everything
    // off the native `change` event, which only a real activation produces.
    target.click();

    // The label's selected value is server-rendered and only catches up when the section response
    // lands. Write it now — the click above just synced the stale value back over the copy.
    const tilesValue = this.#tiles?.querySelector(".variant-option__label-value");
    if (tilesValue) tilesValue.textContent = input.value;
  };

  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest(".buy-buttons-block");
    if (!buyButtonsBlock) return;

    const footer = document.querySelector("footer") ?? document.querySelector(".shopify-section-group-footer-group");
    if (!footer) return;

    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (!entry.isIntersecting && !this.#isSticky) {
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          this.#show();
        }
      } else if (this.#isSticky && entry.isIntersecting) {
        this.#hiddenInFooter = false;
        this.#hide();
      }
    });

    this.#footerSectionObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;
        if (entry.isIntersecting && this.#isSticky) {
          this.#hiddenInFooter = true;
          this.#hide();
        } else if (!entry.isIntersecting && this.#hiddenInFooter) {
          const rect = buyButtonsBlock.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenInFooter = false;
            this.#show();
          }
        }
      },
      {
        rootMargin: "0px 0px -200px 0px",
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#footerSectionObserver.observe(footer);
  }

  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;
    const html = event.detail.data?.html;

    // Ahead of the early return below: a cache hit dispatches with no HTML, so this is the only
    // chance to move the price before the background section fetch lands a frame or two later.
    this.#updateButtonPrice(variant);

    if (!html) return;

    const newStickyAddToCart = html.querySelector("sticky-add-to-cart");
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    const currentSticky = this.refs.stickyBar.getAttribute("data-sticky") || "false";
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // The tiles are ours, not part of the section response, so the morph would delete them.
    // Detach first and re-mount below, against a picker that has already been morphed.
    this.#tiles?.remove();

    // Use default MORPH_OPTIONS (not a plain object) so onAfterUpdate fires,
    // which triggers updatedCallback on responsive-image and re-adds .loaded/.in-view.
    morph(this.refs.stickyBar, newStickyBar);

    this.refs.stickyBar.setAttribute("data-sticky", currentSticky);
    this.dataset.variantAvailable = variantAvailable;

    this.#mountTiles();

    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    const isSoldOut = variant && !variant.available && variant.inventory_management === "shopify";
    this.#updateButtonText(variant?.available, isSoldOut);

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
  };

  #handleVariantSelected = (event) => {
    // Fires synchronously on click, well before the section response — this is what keeps the
    // tiles in the bar in step when the selection was made from the hero picker instead.
    this.#syncTiles();

    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = "";
    const variantTitleElement = this.querySelector(".sticky-add-to-cart__variant");
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    // Check if variant picker uses input radio buttons or select dropdowns
    // They don't exist simultaneously, so we only need to check one type
    const hasInputs = variantPicker.querySelector("input[type='radio']") !== null;
    let selectedOptions = "";

    if (hasInputs) {
      // Get selected values from input radio buttons
      selectedOptions = Array.from(variantPicker.querySelectorAll("input:checked"))
        .map((option) => option.value)
        .filter((value) => value !== "")
        .join(" / ");
    } else {
      // Get selected values from select dropdowns
      selectedOptions = Array.from(variantPicker.querySelectorAll("select.variant-option__select"))
        .map((select) => {
          const selectedOption = select.options[select.selectedIndex];
          return selectedOption ? selectedOption.value : "";
        })
        .filter((value) => value !== "")
        .join(" / ");
    }

    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;

    this.#updateButtonText(false);
  };

  /**
   * Viewport offset so the invalid control sits below the sticky header (not flush/covered).
   * Prefers `--header-height` from `BasicHeader` (#setHeight); falls back to
   * measuring `header[is="sticky-header"]` when the variable is unset or still 0 (e.g. first frame).
   *
   * @returns {number}
   */
  #getConstraintScrollClearancePx() {
    const raw = getComputedStyle(document.documentElement).getPropertyValue("--header-height").trim();
    let headerPx = parseFloat(raw);
    if (!Number.isFinite(headerPx) || headerPx <= 0) {
      const sticky = document.querySelector('header[is="sticky-header"]');
      headerPx = sticky instanceof HTMLElement ? Math.round(sticky.getBoundingClientRect().height) : 0;
    }

    const gapPx = 50;

    return headerPx + gapPx;
  }

  /**
   * @param {HTMLFormElement} form
   * @returns {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null}
   */
  #getFirstInvalidControl(form) {
    for (const el of form.elements) {
      if (
        !(el instanceof HTMLInputElement) &&
        !(el instanceof HTMLSelectElement) &&
        !(el instanceof HTMLTextAreaElement)
      ) {
        continue;
      }
      if (!el.willValidate || el.disabled) continue;
      if (!el.checkValidity()) return el;
    }
    return null;
  }

  /**
   * Browser `requestSubmit` scrolls the invalid field into view while Lenis smooth-scroll is active,
   * which fights native scroll and can jump the page (e.g. toward top) and dismiss the constraint UI.
   * We scroll via Lenis after `focus({ preventScroll: true })`, then call `reportValidity` only after
   * that scroll finishes (`onComplete` when `immediate: false`, or `scrollend` without Lenis).
   *
   * @param {HTMLFormElement} form
   * @param {HTMLButtonElement | undefined} fallbackSubmitter
   */
  #reportInvalidFormConstraints(form, fallbackSubmitter) {
    const invalidEl = this.#getFirstInvalidControl(form);
    if (!invalidEl) {
      if (fallbackSubmitter?.type === "submit") {
        form.requestSubmit(fallbackSubmitter);
      } else {
        form.requestSubmit();
      }
      return;
    }

    const clearancePx = this.#getConstraintScrollClearancePx();

    invalidEl.focus({ preventScroll: true });

    const showBubble = () => {
      requestAnimationFrame(() => {
        invalidEl.reportValidity();
      });
    };

    const lenis = getLenis();
    if (lenis) {
      lenis.scrollTo(invalidEl, {
        offset: -clearancePx,
        immediate: false,
        onComplete: showBubble,
      });
      return;
    }

    const rect = invalidEl.getBoundingClientRect();
    const targetTop = rect.top + window.scrollY - clearancePx;
    window.scrollTo({ top: Math.max(0, targetTop), behavior: "smooth" });

    let settled = false;
    const finishAfterScroll = () => {
      if (settled) return;
      settled = true;
      if (fallbackTimer !== 0) {
        window.clearTimeout(fallbackTimer);
      }
      showBubble();
    };

    let fallbackTimer = 0;
    if ("onscrollend" in window) {
      window.addEventListener("scrollend", finishAfterScroll, { once: true, passive: true });
      fallbackTimer = window.setTimeout(finishAfterScroll, 1200);
    } else {
      fallbackTimer = window.setTimeout(finishAfterScroll, 450);
    }
  }

  handleAddToCartClick = () => {
    const productForm = this.#getProductForm();
    const form = productForm?.querySelector("form");
    const targetButton = productForm?.querySelector('[ref="addToCartButton"]');
    const submitter =
      targetButton instanceof HTMLButtonElement && targetButton.type === "submit" ? targetButton : undefined;

    if (form && !form.checkValidity()) {
      this.#reportInvalidFormConstraints(form, submitter);
      return;
    }

    if (!targetButton) return;
    this.#addLoading();
    targetButton.click();
  };

  #handleCartAddComplete = () => {
    this.#removeLoading();
  };

  #updateButtonText = (isAvailable, isSoldOut = false) => {
    const { addToCartText, addToCartButton, addToCartPrice } = this.refs;
    if (!addToCartText) return;

    if (isAvailable) {
      addToCartText.textContent = addToCartButton.dataset.addToCartText;
    } else if (isSoldOut) {
      addToCartText.textContent = addToCartButton.dataset.soldOutText;
    } else {
      addToCartText.textContent = addToCartButton.dataset.unavailableText;
    }

    // A price next to "Sold out" or "Unavailable" reads as a live offer, so it goes with the
    // Add to cart wording rather than sticking around underneath it.
    if (addToCartPrice) addToCartPrice.hidden = !isAvailable;
  };

  /**
   * Move the price shown inside the Add to cart button. The button is the only place the tiles
   * layout prints a price, so it has to track the selection as closely as the tiles themselves.
   * Server-rendered markup is the source of truth — this just covers the optimistic window.
   *
   * @param {{ available?: boolean, price?: number, compare_at_price?: number | null } | null} variant
   */
  #updateButtonPrice(variant) {
    const { addToCartPrice, addToCartPriceCurrent, addToCartPriceCompare } = this.refs;
    if (!addToCartPrice) return;

    if (!variant?.available || typeof variant.price !== "number") {
      addToCartPrice.hidden = true;
      return;
    }

    const moneyFormat = window.FoxTheme?.moneyFormat || "${{amount}}";

    if (addToCartPriceCurrent) {
      addToCartPriceCurrent.textContent = formatCurrency(variant.price, moneyFormat);
    }

    if (addToCartPriceCompare) {
      // Same test the `price` snippet uses, so a variant priced at its compare-at (or with none
      // at all) shows a single figure here just as it does on the page.
      const { compare_at_price: compareAtPrice } = variant;
      const onSale = typeof compareAtPrice === "number" && compareAtPrice > variant.price;

      if (onSale) addToCartPriceCompare.textContent = formatCurrency(compareAtPrice, moneyFormat);
      addToCartPriceCompare.hidden = !onSale;
    }

    addToCartPrice.hidden = false;
  }

  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest(".shopify-section");
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace("shopify-section-", "");
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  #show() {
    const { stickyBar } = this.refs;
    this.#isSticky = true;
    stickyBar.dataset.sticky = "true";

    // The global reveal IntersectionObserver excludes the bottom 50px (rootMargin -50px),
    // so the fixed sticky bar is never detected as in-viewport by it.
    // Force-add .in-view when the bar becomes visible so zoom-reveal images display correctly.
    const mediaWrapper = stickyBar.querySelector(".sticky-add-to-cart__image .media");
    if (mediaWrapper?.classList.contains("loaded")) {
      mediaWrapper.classList.add("in-view");
    }
  }

  #hide() {
    const { stickyBar } = this.refs;
    this.#isSticky = false;
    stickyBar.dataset.sticky = "false";
  }

  #addLoading() {
    this.refs.addToCartButton.classList.add("btn--loading");
    this.refs.addToCartSpinner.classList.remove("hidden");
  }

  #removeLoading() {
    this.refs.addToCartButton.classList.remove("btn--loading");
    this.refs.addToCartSpinner.classList.add("hidden");
  }
}

if (!customElements.get("sticky-add-to-cart")) {
  customElements.define("sticky-add-to-cart", StickyAddToCart);
}
